import { ExpandLessRounded, ExpandMoreRounded, InfoOutlined } from "@mui/icons-material";
import {
  AccordionDetails,
  Box,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";

const AccordionSummary = styled(MuiAccordionSummary)<{
  $summaryColorOnHover?: string;
  $isExpanded: boolean;
  $hoverActionsAlwaysVisible?: boolean;
}>`
  cursor: pointer;
  min-height: auto;
  transition: ${({ theme }) => theme.transitions.create("background-color")};
  position: relative;
  border-radius: 10px;
  border-bottom-left-radius: ${({ $isExpanded }) => ($isExpanded ? 0 : 10)}px;
  border-bottom-right-radius: ${({ $isExpanded }) => ($isExpanded ? 0 : 10)}px;
  padding: 0px 10px;

  .MuiAccordionSummary-content {
    margin: 0;
  }

  .hoverActions {
    visibility: visible;
    opacity: 1;
    @media (pointer: fine) {
      visibility: ${({ $hoverActionsAlwaysVisible }) => ($hoverActionsAlwaysVisible ? "visible" : "hidden")};
      opacity: ${({ $hoverActionsAlwaysVisible }) => ($hoverActionsAlwaysVisible ? 1 : 0)};
      transition: ${({ theme }) => theme.transitions.create("opacity")};
    }
  }

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 20px;
    background: linear-gradient(
      to bottom,
      ${({ $summaryColorOnHover, theme }) =>
        $summaryColorOnHover ? $summaryColorOnHover : theme.palette.primary.transparent5},
      transparent
    );
    opacity: 0;
    transition: ${({ theme }) => theme.transitions.create("opacity")};
    pointer-events: none;
    z-index: 0;
  }

  &:hover {
    background-color: ${({ $summaryColorOnHover, theme }) =>
      $summaryColorOnHover ? $summaryColorOnHover : theme.palette.primary.transparent5};
    .hoverActions {
      visibility: visible;
      opacity: 1;
    }
    &::after {
      opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
    }
  }
`;

const StyledAccordion = styled(MuiAccordion)<{ $backgroundColor?: string }>`
  box-shadow: none;
  background-color: ${({ $backgroundColor, theme }) => $backgroundColor ?? theme.palette.background.paper};
  border-radius: 10px !important;
  border: solid 1px ${({ theme }) => theme.palette.grey[200]};
  overflow: hidden;
`;

interface IProps {
  children: React.ReactNode;
  title: ((expanded: boolean) => React.ReactNode) | React.ReactNode;
  hintText?: string;
  icon?: React.ReactNode;
  expanded?: boolean;
  initialExpanded?: boolean;
  onExpandClicked?: () => void;
  color?: string;
  backgroundColor?: string;
  sx?: SxProps;
  hoverActions?: React.ReactNode;
  hoverActionsAlwaysVisible?: boolean;
  accordionDetailsSx?: SxProps;
}

const Accordion = (props: IProps) => {
  const [isExpandedInternal, setIsExpandedInternal] = useState(props.expanded ?? props.initialExpanded ?? true);
  const computedBackgroundColor = props.backgroundColor ?? (props.color ? alpha(props.color, 0.05) : undefined);
  const summaryColorOnHover = props.color ? alpha(props.color, 0.1) : undefined;
  const isExpanded = props.expanded !== undefined ? props.expanded : isExpandedInternal;
  const theme = useTheme();

  function isTextNode(value: React.ReactNode): value is string | number {
    return typeof value === "string" || typeof value === "number";
  }

  function renderTitle(title: ((expanded: boolean) => React.ReactNode) | React.ReactNode) {
    const titleValue = typeof title === "function" ? title(isExpanded) : title;

    if (isTextNode(titleValue)) {
      return <Typography variant="body2">{titleValue}</Typography>;
    }

    return titleValue;
  }

  const handleExpandClick = () => {
    if (props.onExpandClicked) {
      props.onExpandClicked();
    } else {
      setIsExpandedInternal((value) => !value);
    }
  };

  return (
    <StyledAccordion disableGutters expanded={isExpanded} $backgroundColor={computedBackgroundColor} sx={props.sx}>
      <AccordionSummary
        as="div"
        $summaryColorOnHover={summaryColorOnHover}
        $isExpanded={isExpanded}
        $hoverActionsAlwaysVisible={props.hoverActionsAlwaysVisible}
        onClick={() => handleExpandClick()}
      >
        <Stack direction="row" spacing={0.25} sx={{ alignItems: "center", width: "100%", zIndex: 2 }}>
          {isExpanded ? <ExpandLessRounded fontSize="large" /> : <ExpandMoreRounded fontSize="large" />}
          <Stack direction="row" sx={{ alignItems: "center", minHeight: "35px", width: "100%" }}>
            {/* Title Content */}
            <Stack
              direction="row"
              spacing={0.25}
              sx={{
                flexGrow: 1,
                alignItems: "top"
              }}>
              {props.icon && <Box sx={{ display: "flex", alignSelf: "center" }}>{props.icon}</Box>}
              {renderTitle(props.title)}
              {props.hintText && (
                <Tooltip title={props.hintText}>
                  <InfoOutlined sx={{ color: theme.palette.text.secondary, alignSelf: "center" }} />
                </Tooltip>
              )}
            </Stack>

            {props.hoverActions && (
              <Box
                className="hoverActions"
                onClick={(event) => event.stopPropagation()}
                onFocus={(event) => event.stopPropagation()}
              >
                {props.hoverActions}
              </Box>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, ...props.accordionDetailsSx }}>{props.children}</AccordionDetails>
    </StyledAccordion>
  );
};

export { Accordion };
